import type { CommonAxiosPayload } from '@services/types'

import type {
  TaxationAtalanyAutotaxMainData,
  TaxationKataAutotaxMainData,
  TaxationLimitCard,
} from './TaxationAutotaxWidgets/elements/types'
import type { TaxationTaskData } from './TaxationTasks/types'
import type { VatPeriodOptions } from './VatPositionReportAccordion/types'

export enum PulseDialogs {
  WELCOME_TO_TASK_LIST = 'welcomeToTaskList',
}

export enum ReportNames {
  BANK_TRANSACTIONS = 'bank-transactions',
  DISCLAIMER = 'disclaimer',
  KPI_WIDGET = 'kpi-widget',
  PULSE_MONTHLY_CHART = 'pulse-monthly-chart',
  QUIC_CHAT = 'quick-chat',
  SETUP_TASK_LIST = 'setup-task-list',
  SPLITTER = 'splitter',
  TAXATION_AAM = 'taxation-aam',
  TAXATION_ATALANY = 'taxation-atalany',
  TAXATION_ATALANY_AUTOTAX = 'taxation-atalany-autotax',
  TAXATION_KATA = 'taxation-kata',
  TAXATION_KATA_AUTOTAX = 'taxation-kata-autotax',
  TAXATION_TASK = 'taxation-task',
  TAXATION_TAX_CALCULATION = 'taxation-tax-calculation',
  VAT_POSITION = 'vat-position',
}

export enum SetupTaskNames {
  CONNECT_INTEGRATION = 'connect-integration',
  INVITE_ACCOUNTANT = 'invite-accountant',
  SETUP_TAXATION_AAM = 'setup-taxation-aam',
  SETUP_TAXATION_TAX = 'setup-taxation-tax-form',
}

interface BackendBankTransactionsReport {
  data: { summary: number; show_promo: boolean }
  id: string
  name: ReportNames.BANK_TRANSACTIONS
}

interface BackendVatPositionReport {
  data: { [key in VatPeriodOptions]: { vat: number } }
  id: string
  name: ReportNames.VAT_POSITION
}

interface BackendTaxationTask {
  data: TaxationTaskData
  id: string
  name: ReportNames.TAXATION_TASK
}

export interface BackendSetupTaskCommonProps {
  metadata: unknown // we will always have this, but might be empty object (ie. when status === 'disabled')
  status: 'current' | 'skipped' | 'completed' | 'disabled'
  links: {
    complete?: string
    skip?: string
  } // it will be an empty object if status === 'disabled'
}

export interface BackendConnectIntegrationTask extends BackendSetupTaskCommonProps {
  name: SetupTaskNames.CONNECT_INTEGRATION
  metadata: {
    company_type: string
    is_szamlazz_connected: boolean
    is_billingo_connected: boolean
  }
}

export interface BackendInviteAccountantTask extends BackendSetupTaskCommonProps {
  name: SetupTaskNames.INVITE_ACCOUNTANT
  links: {
    complete: string
    skip: string
  }
}

export interface BackendSetupTaxationAamTask extends BackendSetupTaskCommonProps {
  name: SetupTaskNames.SETUP_TAXATION_AAM
  metadata: {
    founded_at: Nullable<string>
    tax_number: string
  }
}

export interface BackendSetupTaxationTaxTask extends BackendSetupTaskCommonProps {
  name: SetupTaskNames.SETUP_TAXATION_TAX
  metadata: {
    atalany_cost_percentage?: Nullable<string>
    atalany_employment_type?: Nullable<string>
    atalany_qualification_required?: Nullable<boolean>
    partner_company_tax_number?: Nullable<string>
    savos_ipa?: Nullable<boolean>
    savos_ipa_type?: Nullable<number>
    start_date?: Nullable<string>
    tax_form?: Nullable<string>
  }
}

export type BackendSetupTask =
  | BackendConnectIntegrationTask
  | BackendInviteAccountantTask
  | BackendSetupTaxationAamTask
  | BackendSetupTaxationTaxTask

export interface BackendSetupTaskList {
  data: {
    tasks: BackendSetupTask[]
  }
  id: string
  name: ReportNames.SETUP_TASK_LIST
}

export interface PulseSplitter {
  data: string | number
  id: string
  name: ReportNames.SPLITTER
}

export interface PulseDisclaimer {
  data: string
  id: string
  name: ReportNames.DISCLAIMER
}

//* EV specifix reports
export interface BackendEvKataWidget {
  id: string
  name: ReportNames.TAXATION_KATA_AUTOTAX
  data: Nullable<{
    kata_used_amount: number
  }>
}

export interface BackendEvAamWidget {
  id: string
  name: ReportNames.TAXATION_ATALANY_AUTOTAX
  data: Nullable<{
    aam_used_amount: number
  }>
}

export type BankTransactionsReportAccordionProps = ReportComponentProps<BackendBankTransactionsReport>

export type VatPositionReportAccordionProps = ReportComponentProps<BackendVatPositionReport>

export type SetupTaskListProps = ReportComponentProps<BackendSetupTaskList> & {
  closeTask: AsyncFunction<CommonAxiosPayload<unknown>, BackendSetupTaskList['data']>
}

interface TaxationReportDetails {
  links: { details: string }
}

export type ReportAccordionOpenHandler = (cardId: string, isExpanding: boolean) => void

//* generic report card props
export interface ReportComponentProps<ReportData> {
  companyCurrency: string
  initialOpenHandler: (cardId: string) => void
  open: boolean
  openHandler: ReportAccordionOpenHandler
  refetchReports: VoidFunction
  report: ReportData
  updatedAt: string
}

//* Taxation placeholders
interface BackendTaxationAamReport extends TaxationReportDetails {
  id: string
  name: ReportNames.TAXATION_AAM
  data: {
    aam_reducing: number
    aam_reducing_pct: number
    aam_remaining: number
    is_current: boolean
    year: number
  }
}

interface BackendTaxationKataReport extends TaxationReportDetails {
  id: string
  name: ReportNames.TAXATION_KATA
  data: {
    kata_reducing: number
    kata_reducing_pct: number
    kata_remaining: number
    is_current: boolean
    year: number
  }
}

interface BackendTaxationAtalanyReport extends TaxationReportDetails {
  id: string
  name: ReportNames.TAXATION_ATALANY
  data: {
    atalany_reducing: number
    atalany_reducing_pct: number
    atalany_remaining: number
    is_current: boolean
    year: number
  }
}

export type ReportYearlyLink = { url: string; year: number }

export type TaxationAamAccordionProps = ReportComponentProps<BackendTaxationAamReport>

export type TaxationKataAccordionProps = ReportComponentProps<BackendTaxationKataReport>

export type TaxationAtalanyAccordionProps = ReportComponentProps<BackendTaxationAtalanyReport>

interface BackendTaxationTaxCalculationReport {
  id: string
  name: ReportNames.TAXATION_TAX_CALCULATION
  data: {
    current_date: string
    payable_tax: number
    year: number
    current_quarter: number | null
  }
  links: {
    monthly: ReportYearlyLink[]
    payable: ReportYearlyLink[]
  }
}

export type TaxationTaxCalculationAccordionProps = ReportComponentProps<BackendTaxationTaxCalculationReport>

interface TaxationAtalanyAutotaxData extends TaxationAtalanyAutotaxMainData {
  cards: TaxationLimitCard[]
}

interface BackendTaxationAtalanyAutotaxReport {
  id: string
  name: ReportNames.TAXATION_ATALANY_AUTOTAX
  data: TaxationAtalanyAutotaxData
}

export type TaxationAtalanyAutotaxWidgetProps = ReportComponentProps<BackendTaxationAtalanyAutotaxReport>

interface TaxationKataAutotaxData extends TaxationKataAutotaxMainData {
  cards: TaxationLimitCard[]
}

interface BackendTaxationKataAutotaxReport {
  id: string
  name: ReportNames.TAXATION_KATA_AUTOTAX
  data: TaxationKataAutotaxData
}

export type TaxationKataAutotaxWidgetProps = ReportComponentProps<BackendTaxationKataAutotaxReport>

export enum PulseMonthlyChartDisplayType {
  GROSS = 'gross',
  NET = 'net',
}

export interface BackendPulseMonthlyChartReport {
  id: string
  name: ReportNames.PULSE_MONTHLY_CHART
  data: { display: PulseMonthlyChartDisplayType }
  links: {
    gross: ReportYearlyLink
    net: Nullable<ReportYearlyLink>
  }
}

export type PulseMonthlyChartAccordionProps = ReportComponentProps<BackendPulseMonthlyChartReport>

//* DYNAMIC TITLE VALUES

export type TaxationTitleValues = {
  isCurrent: boolean
  reducingPercent: number
  remainingAmount: number
  usedAmount: number
  year: number
}

export type BankTransactionsTitleValues = {
  amount: number
}

export type TaxationTaxCalculationTitleValues = {
  currentDate: string
  payableTax: Decimal | number
}

export interface IncomeRegister {
  params: {
    start_date: string
    end_date: string
  }
}

export interface PulseKPIWidget {
  name: ReportNames.KPI_WIDGET
  data: {
    operation_results: {
      expense: number
      income: number
      total: number
    }
    outstandings: {
      expense_outstanding: number
      income_outstanding: number
      total: number
    }
    vat: Nullable<{
      expense_vat: number
      income_vat: number
      total: number
      vat_period: 'monthly' | 'quarterly' | 'yearly'
    }>
  }
}

//* CHAT
export type QuickChatData = {
  name: ReportNames.QUIC_CHAT
  data: unknown // not used yet
  links: {
    chat: string
  }
}

export type BackendReportData =
  | BackendVatPositionReport
  | BackendBankTransactionsReport
  | BackendSetupTaskList
  | PulseSplitter
  | BackendEvKataWidget
  | BackendEvAamWidget
  | PulseDisclaimer
  | BackendTaxationTask
  | BackendTaxationTaxCalculationReport

export type BackendReportResults = {
  links: {
    more_reports: BackendCallableLink
  }
  reports: BackendReportData[]
}

export interface FetchReportsHookProps {
  callUrl: AsyncFunction<CommonAxiosPayload, BackendReportResults>
  fetchReports: AsyncFunction<void, BackendReportResults>
}
